import { Box } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import Layout from "../../../components/layout/Layout";
import { ClientContext } from "../../../state/ClientContext";
import {
  getActivityCompletionButtonClass,
  getActivityCompletionLabel,
  resolveActivityCompletionState,
  shouldShowActivityCompletionBadge,
} from "../../../utils/activityCompletionState";
// GMA-1228: shared client-zone formatter; keeps tooltip / CSV / grid / view
// page on a single source of truth (see utils/activityTimezone.js).
import { formatZonedWallTime } from "../../../utils/activityTimezone";
import "./ViewActivityPage.css";

const ACTIVITY_VIEW_FORMAT = "M/D/YY [at] h:mm A";

const BASE_URL = process.env.REACT_APP_BASE_URL;

export const ViewActivityPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // GMA-1228: pull the client zone from context so start/end/declared-not-done
  // render in the same wall-clock time as the calendar grid and tooltips. The
  // page is normally reached from the calendar (context is present) but
  // defensive-destructure so tests and direct-URL loads do not crash when
  // there is no ClientProvider in the tree.
  const clientContext = useContext(ClientContext);
  const selectedClient = clientContext?.selectedClient;
  const clientZone = selectedClient?.timezone;

  const [activity, setActivity] = useState(null);
  const [clientId, setClientId] = useState(null);

  useEffect(() => {
    if (location.state?.activity) {
      setActivity(location.state?.activity);
    }
  }, [location.state?.activity]);

  useEffect(() => {
    if (location.state?.clientId) {
      setClientId(location.state?.clientId);
    }
  }, [location.state?.clientId]);

  const handleEdit = async () => {
    const stepsResponse = await fetch(
      `${BASE_URL}/event_steps/${activity.id}`,
      {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
        },
      },
    );
    const stepsData = await stepsResponse.json();
    const event = {
      ...activity,
      clientId: clientId,
      steps: stepsData.steps ?? [],
    };

    navigate(`/add-activity`, {
      state: {
        event: event,
      },
    });
  };

  const handleClose = () => {
    navigate(`/calendar/${activity.user_id}`);
  };

  const completionState = resolveActivityCompletionState(activity);
  const showCompletionBadge = shouldShowActivityCompletionBadge(activity);
  const completionLabel = getActivityCompletionLabel(activity);
  const completionButtonClass = getActivityCompletionButtonClass(activity);
  const declaredNotDoneAt = activity?.client_marked_not_done_at;

  return (
    <>
      <Layout className="bg-black" showSideNav={false}>
        <div className="view-activity-container">
          <div className="view-activity-header">
            <h1 className="view-activity-title">View activity</h1>
            {showCompletionBadge && (
              <button
                className={completionButtonClass}
                data-completion-state={completionState}
              >
                <strong>{completionLabel}</strong>
              </button>
            )}
          </div>

          <div className="card">
            <h2 className="details-title">Activity details</h2>
            <table className="details-table">
              <tbody>
                <tr>
                  <th>Name</th>
                  <td>{activity?.title}</td>
                </tr>
                {activity?.description && (
                  <tr>
                    <th>Description</th>
                    <td>
                      <div
                        className="markdown-content"
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(activity.description),
                        }}
                      />
                    </td>
                  </tr>
                )}
                <tr>
                  <th>Start</th>
                  <td>
                    {activity &&
                      formatZonedWallTime(
                        activity?.start_time,
                        clientZone,
                        ACTIVITY_VIEW_FORMAT,
                        { source: "view-activity", clientId: selectedClient?.id },
                      )}
                  </td>
                </tr>
                <tr>
                  <th>End</th>
                  <td>
                    {activity &&
                      formatZonedWallTime(
                        activity?.end_time,
                        clientZone,
                        ACTIVITY_VIEW_FORMAT,
                        { source: "view-activity", clientId: selectedClient?.id },
                      )}
                  </td>
                </tr>
                {declaredNotDoneAt && (
                  <tr>
                    <th>Client marked not done</th>
                    <td>
                      {formatZonedWallTime(
                        declaredNotDoneAt,
                        clientZone,
                        ACTIVITY_VIEW_FORMAT,
                        { source: "view-activity", clientId: selectedClient?.id },
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <Box sx={{ mb: 4, mx: 3, display: "flex", justifyContent: "flex-end" }}>
          {activity?.isEditable && (
            <button
              className="close-button"
              onClick={handleEdit}
              style={{ margin: "0 10px" }}
            >
              <strong>Edit</strong>
            </button>
          )}
          <button className="close-button" onClick={handleClose}>
            <strong>Close</strong>
          </button>
        </Box>
      </Layout>
    </>
  );
};
